import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteIcon from '@mui/icons-material/Delete';
import { FormType } from 'app/pages/cards/cards/NewCardSheet/newCardUtils';
import { ModalType } from 'app/shared-components/ModalActionsCard/ModalActionsCard';
import { CardsParams } from 'app/pages/cards/AdminCardsDisplay';
import { useAppDispatch } from 'app/store';
import { useTranslation } from 'react-i18next';
import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MoreVertMenuOptionProps } from 'app/shared-components/MoreVertMenu/MoreVertMenu';
import { getCard, getCardList } from 'app/pages/store/CardDataSlice/cardsThunks';
import { SidesheetRoutes, useSidesheet } from 'app/shared-components/sidesheets/SidesheetProvider';
import { HNCardFormFactor, HNPaymentCardStatus } from 'API';
import type { GetCardsPayload } from 'CDK_API';
import {
  CreditCard as CreditCardIcon,
  EditOutlined as EditIcon,
  Error as ErrorIcon,
  Lock,
  LockOpen,
  Person,
} from '@mui/icons-material';
import { resetOpenCardDetail } from 'app/pages/store/CardDataSlice/cardDataSlice';

interface cardTableUtilsProps {
  isGroupTable?: boolean;
}

// TODO - make a context and a provider for this so we can stop passing these props around
export const useCardTableUtils = ({ isGroupTable = false }: cardTableUtilsProps) => {
  const [modalType, setModalType] = useState<ModalType>(ModalType.lock);
  const [selectedPaymentCard, setSelectedPaymentCard] = useState<GetCardsPayload>();
  const [panelCardActionsOpen, setPanelCardActionsOpen] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { openSidesheet } = useSidesheet();

  const params = useParams<CardsParams>();
  const { groupId } = params;

  const loadUserCardsList = useCallback(
    (paidolId: string, userId?: string) => {
      dispatch(
        getCardList({
          filter: {
            paidolId: { eq: paidolId },
            userId: { eq: userId },
          },
        })
      );
    },
    [dispatch]
  );

  const handleOpenAssignUserModal = useCallback(
    (paymentCard: GetCardsPayload) => {
      if (!paymentCard.paymentCardId || !paymentCard.paidolId) return;

      openSidesheet(SidesheetRoutes.AssignCard, {
        paymentCardId: paymentCard.paymentCardId,
        onSuccess: () =>
          paymentCard &&
          dispatch(
            getCard({
              filter: {
                and: [{ or: [{ paymentCardId: { eq: paymentCard.paymentCardId } }] }],
                paidolId: { eq: paymentCard.paidolId },
              },
            })
          ),
      });
    },
    [dispatch, openSidesheet]
  );

  const handleOrderPhysicalCard = useCallback(
    (paymentCard: GetCardsPayload) => {
      dispatch(resetOpenCardDetail());
      openSidesheet(SidesheetRoutes.NewCard, {
        formType: FormType.OrderPhysical,
        selectedPaymentCard: paymentCard,
      });
    },
    [dispatch, openSidesheet]
  );

  const handleUpdateCard = useCallback(
    (paymentCard: GetCardsPayload) => {
      dispatch(resetOpenCardDetail());
      openSidesheet(SidesheetRoutes.NewCard, {
        formType: FormType.UpdateCard,
        selectedPaymentCard: paymentCard,
        onUpdateCard: async () => {
          // SEE-904: refetch only the edited card. The getCard.fulfilled
          // reducer patches it into paymentCardList in place, preserving the
          // user's scroll position and visible-window count. The previous
          // dispatch(getCardList(...)) here threw away accumulated pages.
          if (!paymentCard.paymentCardId || !paymentCard.paidolId) return;
          try {
            await dispatch(
              getCard({
                filter: {
                  and: [{ or: [{ paymentCardId: { eq: paymentCard.paymentCardId } }] }],
                  paidolId: { eq: paymentCard.paidolId },
                },
              })
            );
          } catch (error) {
            console.error('Error refreshing card data from table:', error);
          }
        },
      });
    },
    [dispatch, openSidesheet]
  );

  const handleClickNewCard = () => {
    dispatch(resetOpenCardDetail());
    openSidesheet(SidesheetRoutes.NewCard, {
      formType: FormType.IssueCard,
    });
  };

  const handleClickAssignCard = useCallback(
    (e: React.MouseEvent<HTMLElement>, paymentCard: GetCardsPayload) => {
      e.stopPropagation();
      handleOpenAssignUserModal(paymentCard);
    },
    [handleOpenAssignUserModal]
  );

  const handleClickCardActions = useCallback((type: ModalType, paymentCard: GetCardsPayload) => {
    setSelectedPaymentCard(paymentCard);
    setModalType(type);
    setPanelCardActionsOpen(true);
  }, []);

  const handleCardClick = useCallback(
    (paymentCardId: string) => {
      navigate(`/cards/payment-cards/card/${paymentCardId}`);
    },
    [navigate]
  );

  const handleCardClickGroups = useCallback(
    (paymentCardId: string) => {
      navigate(`/team/card-groups/${groupId}/cards/${paymentCardId}`);
    },
    [navigate, groupId]
  );

  const getMoreMenuOptions = useCallback(
    (paymentCard: GetCardsPayload): MoreVertMenuOptionProps[] => {
      const options = [];

      if (paymentCard.status === HNPaymentCardStatus.CLOSED) {
        if (isGroupTable) {
          options.push({
            label: t('cardActions.removeFromGroup'),
            Icon: <DeleteIcon color="primary" />,
            onClick: () => handleClickCardActions(ModalType.removeFromGroup, paymentCard),
          });
        }

        options.push({
          label: t('cardActions.assignToGroup'),
          Icon: <ArrowForwardIcon color="primary" />,
          onClick: () => handleClickCardActions(ModalType.moveToAnotherGroup, paymentCard),
        });

        return options;
      }

      // For all other statuses, show full set of actions
      const isPhysical = paymentCard.formFactor === HNCardFormFactor.PHYSICAL;
      const isAssigned = !!paymentCard.cardholder;
      const isExpired = new Date(paymentCard.expirationDate) < new Date();

      if (!isExpired) {
        if (paymentCard.isAuthorizedUserCard === false) {
          options.push({
            label: t('cardActions.assignCard'),
            Icon: <Person color="primary" />,
            onClick: (e: React.MouseEvent<HTMLElement>) => handleClickAssignCard(e, paymentCard),
          });
        }

        if (paymentCard.status === HNPaymentCardStatus.SUSPENDED) {
          options.push({
            label: t('cardActions.unlockCard'),
            Icon: <LockOpen color="primary" />,
            onClick: () => handleClickCardActions(ModalType.unlock, paymentCard),
          });
        }

        if (paymentCard.status === HNPaymentCardStatus.ACTIVE) {
          options.push({
            label: t('cardActions.lockCard'),
            Icon: <Lock color="primary" />,
            onClick: () => handleClickCardActions(ModalType.lock, paymentCard),
          });
        }

        if (!isPhysical && isAssigned) {
          options.push({
            label: t('cardActions.orderPhysicalCard'),
            Icon: <CreditCardIcon color="primary" />,
            onClick: () => handleOrderPhysicalCard(paymentCard),
          });
        }

        if (isAssigned) {
          options.push({
            label: t('cardActions.editCard'),
            Icon: <EditIcon color="primary" />,
            onClick: () => handleUpdateCard(paymentCard),
          });
        }
      }

      options.push({
        label: t('cardActions.cancelCard'),
        Icon: <ErrorIcon color="primary" />,
        onClick: () => handleClickCardActions(ModalType.cancel, paymentCard),
      });

      if (isGroupTable) {
        options.push({
          label: t('cardActions.removeFromGroup'),
          Icon: <DeleteIcon color="primary" />,
          onClick: () => handleClickCardActions(ModalType.removeFromGroup, paymentCard),
        });
      }

      options.push({
        label: t('cardActions.assignToGroup'),
        Icon: <ArrowForwardIcon color="primary" />,
        onClick: () => handleClickCardActions(ModalType.moveToAnotherGroup, paymentCard),
      });

      return options;
    },
    [
      handleClickAssignCard,
      handleClickCardActions,
      handleOrderPhysicalCard,
      handleUpdateCard,
      isGroupTable,
      t,
    ]
  );

  return {
    getMoreMenuOptions,
    loadUserCardsList,
    handleCardClick,
    handleCardClickGroups,
    handleClickNewCard,
    handleOpenAssignUserModal,
    handleUpdateCard,
    modalType,
    panelCardActionsOpen,
    selectedPaymentCard,
    setPanelCardActionsOpen,
  };
};
